import { useMemo } from 'react';
import { Formik } from 'formik';
import { map } from 'lodash';
import moment from 'moment';
import type {
  Cutsheet,
  EnumCutsheetAnimalSpecies,
  EnumCutsheetInspectionLevels,
  ProcessorCapability,
  ProcessorSettings,
} from 'lib/graphql';
import { EnumCutsheetTrimTrimSelection } from 'lib/graphql';
import type { CutsheetWizardFormValues, WizardPrimalCut } from '../types';
import { WizardConfigProvider } from '../context';
import { WizardStepsBuilder } from './wizard-steps-builder';
import type { CutsheetRequest } from 'pages/scheduling/_views/edit-cutsheets';

export type CutsheetWizardProps = {
  open: boolean;
  onClose: () => void;
  capabilities?: ProcessorCapability[];
  settings?: ProcessorSettings;
  animalSpecies?: EnumCutsheetAnimalSpecies;
  inspectionLevel?: EnumCutsheetInspectionLevels;
  isPreviewMode?: boolean;
  renderInline?: boolean;
  onCutsheetCreated?: () => void;
  onSubmitWithCutsheet?: (cutsheet: Cutsheet, selectedHeads: string[]) => void;
  userCutsheetRequests?: CutsheetRequest[];
  templateCutsheet?: Cutsheet; // Existing cutsheet to use as template
  onUseAdvanced?: () => void; // Callback to switch to advanced view
  cutsheetType?: 'vendor' | 'producer' | 'buyer' | 'user';
  userId?: string; // User ID for buyer/producer cutsheets
  buyerId?: string; // Buyer ID when there's a single buyer
  producerId?: string; // Producer ID for producer cutsheets
};

export function CutsheetWizardForm({
  open,
  onClose,
  settings = undefined,
  capabilities = undefined,
  animalSpecies = undefined,
  inspectionLevel = undefined,
  isPreviewMode = false,
  renderInline = false,
  onCutsheetCreated = undefined,
  onSubmitWithCutsheet = undefined,
  userCutsheetRequests = undefined,
  templateCutsheet = undefined,
  onUseAdvanced = undefined,
  cutsheetType = undefined,
  userId = undefined,
  buyerId = undefined,
  producerId = undefined,
}: CutsheetWizardProps) {
  // Derive trimSelection from the active capability with explicit fallback
  const activeCapability = useMemo(
    () =>
      (capabilities ?? []).find(
        (cap) =>
          cap?.animalSpecies === animalSpecies &&
          cap?.inspectionLevel === inspectionLevel,
      ),
    [capabilities, animalSpecies, inspectionLevel],
  );

  // Calculate default specifications
  const defaultSpecifications = useMemo(() => {
    const spec = activeCapability?.activeSpecification;
    const raw = spec?.specifications ?? [];
    const defaults: Record<string, string> = {};

    raw.forEach((s) => {
      const key = s?.fid ?? s?.name ?? s?._id ?? '';
      if (key && s?.isVisible !== false) {
        const defaultOption = s?.options?.find((opt) => opt?.isDefault);
        defaults[key] = String(defaultOption?._id ?? defaultOption?.name ?? '');
      }
    });

    return defaults;
  }, [activeCapability]);

  // Transform template cutsheet to form values
  const templateValues =
    useMemo((): Partial<CutsheetWizardFormValues> | null => {
      if (!templateCutsheet) return null;

      // Map specification names to IDs for the wizard
      const mappedSpecifications: Record<string, string> = {};
      const spec = activeCapability?.activeSpecification;
      const specifications = spec?.specifications ?? [];

      if (templateCutsheet.selectedSpecifications) {
        Object.entries(templateCutsheet.selectedSpecifications).forEach(
          ([key, value]) => {
            // Find the specification by fid, name, or _id
            const specification = specifications.find(
              (s) => s?.fid === key || s?.name === key || s?._id === key,
            );

            if (specification) {
              // Use the specification's fid, name, or _id as the key (same as defaultSpecifications)
              const specKey =
                specification?.fid ??
                specification?.name ??
                specification?._id ??
                '';

              if (specKey) {
                // Value might be an option name or ID - try to find the matching option ID
                const matchingOption = specification?.options?.find(
                  (opt) => opt?._id === value || opt?.name === value,
                );

                mappedSpecifications[specKey] = String(
                  matchingOption?._id ?? value ?? '',
                );
              }
            }
          },
        );
      }

      // Strip existing date in brackets from the end of the name
      const baseName = templateCutsheet.name
        .replace(/\s*\(\d{1,2}\/\d{1,2}\/\d{4}\)\s*$/, '')
        .trim();

      // Format current date as MM/DD/YYYY
      const dateStr = moment().format('MM/DD/YYYY');

      return {
        name: `${baseName} (${dateStr})`,
        cuts: (templateCutsheet.primalCuts as WizardPrimalCut[]) || [],
        extras: map(templateCutsheet.extras, (extra) => ({
          _id: extra?._id,
          name: extra?.name ?? '',
          type: extra?.type ?? 'pricedPerPound',
          pricePerPound: extra?.pricePerPound ?? undefined,
          fee: extra?.fee ?? undefined,
          minLbs: extra?.minLbs ?? undefined,
          isActive: extra?.isActive ?? false,
          quantity: extra?.quantity ?? undefined,
        })),
        trim: map(templateCutsheet.trim, (trim) => ({
          name: trim?.name ?? '',
          pricePerPound: trim?.pricePerPound ?? 0,
          minLbs: trim?.minLbs ?? undefined,
          isActive: trim?.isActive ?? false,
          quantity: trim?.quantity ?? undefined,
          rank: trim?.rank ?? 1,
          trimSelection:
            (trim?.trimSelection as EnumCutsheetTrimTrimSelection) ||
            EnumCutsheetTrimTrimSelection.AllowSelectionByWeight,
        })),
        selectedSpecifications: mappedSpecifications,
        notes: templateCutsheet.notes ?? {},
      };
    }, [templateCutsheet, activeCapability]);

  const initialValues: CutsheetWizardFormValues = {
    name: templateValues?.name ?? '',
    cuts: templateValues?.cuts ?? [],
    extras: templateValues?.extras ?? [],
    trim: templateValues?.trim ?? [],
    selectedSpecifications:
      templateValues?.selectedSpecifications ?? defaultSpecifications,
    animalSpecies,
    blockedBy: [],
    inspectionLevel,
    canSelectMoreThanOneTrim:
      activeCapability?.canSelectMoreThanOneTrim ?? true,
    trimSelection: activeCapability?.trimSelection || 'allowSelectionByWeight',
    selectedHeads: [],
    notes: templateValues?.notes ?? {},
  };

  return (
    <Formik<CutsheetWizardFormValues>
      key={`${open ? 'open' : 'closed'}-${templateCutsheet?._id ?? 'new'}`} // Force remount when dialog state or template changes
      initialValues={initialValues}
      enableReinitialize
      onSubmit={() => {
        // no-op for now; steps will mutate values via useFormikContext later
      }}
    >
      <WizardConfigProvider
        capabilities={capabilities}
        settings={settings}
        animalSpecies={animalSpecies}
        inspectionLevel={inspectionLevel}
      >
        <WizardStepsBuilder
          open={open}
          onClose={onClose}
          isPreviewMode={isPreviewMode}
          renderInline={renderInline}
          onCutsheetCreated={onCutsheetCreated}
          onSubmitWithCutsheet={onSubmitWithCutsheet}
          userCutsheetRequests={userCutsheetRequests}
          onUseAdvanced={onUseAdvanced}
          cutsheetType={cutsheetType}
          userId={userId}
          buyerId={buyerId}
          producerId={producerId}
          templateCutsheet={templateCutsheet}
        />
      </WizardConfigProvider>
    </Formik>
  );
}
